/**
 * Runtime configuration accessor.
 *
 * Reads values from `window.__APP_CONFIG__` (populated by `/runtime-config.js`,
 * which the embedded server emits dynamically at request time — no build-time
 * substitution needed). Falls back to Vite build-time `import.meta.env.VITE_*`
 * so that `vite dev` and `vite preview` still work without the server.
 *
 * The bundled SPA is served as a static asset, so build-time env injection
 * (Vite's default) means a config change requires a rebuild. Reading from
 * `runtime-config.js` decouples the bundle from the environment — the same
 * artifact deploys to dev / staging / prod.
 *
 * @see server/index.ts — configures @bcecloud/spa-server's /runtime-config.js
 */

/** Keys present in the runtime-config document for external-client. */
type RuntimeConfigKey =
  | 'CLERK_PUBLISHABLE_KEY'
  | 'STAFF_CLERK_PUBLISHABLE_KEY'
  | 'API_BASE_URL';

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<Record<RuntimeConfigKey, string>>;
  }
}

/**
 * Returns true when a value is an unsubstituted placeholder of the form
 * `__SOMETHING__`. These are emitted by the legacy build-time substitution
 * step and indicate the helper should fall back to the Vite build-time env.
 *
 * @param value - The candidate value.
 */
function isUnsubstitutedPlaceholder(value: string): boolean {
  return /^__[A-Z0-9_]+__$/.test(value);
}

/**
 * Reads a runtime config value, preferring `window.__APP_CONFIG__` and
 * falling back to the build-time `import.meta.env.VITE_<KEY>`.
 *
 * @param key - The config key (e.g. `'CLERK_PUBLISHABLE_KEY'`).
 * @returns The resolved string value, or `undefined` if neither source has it.
 */
export function getRuntimeConfig(key: RuntimeConfigKey): string | undefined {
  const fromWindow = typeof window !== 'undefined' ? window.__APP_CONFIG__?.[key] : undefined;
  if (fromWindow && !isUnsubstitutedPlaceholder(fromWindow)) {
    return fromWindow;
  }

  const env = import.meta.env as Record<string, string | undefined>;
  const fromBuild = env[`VITE_${key}`];
  if (fromBuild && !isUnsubstitutedPlaceholder(fromBuild)) {
    return fromBuild;
  }

  return undefined;
}
